import React, { useState, useEffect } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CirclePlus, Pencil, Plus, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';

interface Category {
  id: string;
  name: string;
  description: string | null;
  parent_id: string | null;
  color: string | null;
  display_order: number;
}

export function CategoryManager() {
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Category | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parentId, setParentId] = useState<string | null>(null);
  const [color, setColor] = useState('#3b82f6');
  const [displayOrder, setDisplayOrder] = useState(0);
  const { toast } = useToast();

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('email_template_categories')
        .select('*')
        .order('display_order');
      
      if (error) throw error;
      setCategories(data || []);
    } catch (error: any) {
      console.error('Error fetching categories:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to load categories',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (category: Category) => {
    setEditingCategory(category);
    setName(category.name);
    setDescription(category.description || '');
    setParentId(category.parent_id);
    setColor(category.color || '#3b82f6');
    setDisplayOrder(category.display_order);
    setShowDialog(true);
  };

  const handleNewCategory = () => {
    setEditingCategory(null);
    setName('');
    setDescription('');
    setParentId(null);
    setColor('#3b82f6');
    setDisplayOrder(categories.length);
    setShowDialog(true);
  };

  const handleAddSubcategory = (parentCategory: Category) => {
    setEditingCategory(null);
    setName('');
    setDescription('');
    setParentId(parentCategory.id);
    setColor(parentCategory.color || '#3b82f6');
    setDisplayOrder(categories.length);
    setShowDialog(true);
  };

  const handleSave = async () => {
    try {
      if (!name) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: 'Category name is required',
        });
        return;
      }

      if (editingCategory) {
        // Update existing category
        const { error } = await supabase
          .from('email_template_categories')
          .update({
            name,
            description: description || null,
            parent_id: parentId,
            color,
            display_order: displayOrder,
          })
          .eq('id', editingCategory.id);

        if (error) throw error;
        toast({
          title: 'Category Updated',
          description: `Category "${name}" has been updated`,
        });
      } else {
        // Create new category
        const { error } = await supabase
          .from('email_template_categories')
          .insert({
            name,
            description: description || null,
            parent_id: parentId,
            color,
            display_order: displayOrder,
          });

        if (error) throw error;
        toast({
          title: 'Category Created',
          description: `Category "${name}" has been created`,
        });
      }
      
      setShowDialog(false);
      fetchCategories();
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to save category',
      });
    }
  };

  const handleDelete = async (id: string, name: string) => {
    try {
      // Check if the category has child categories
      const { data: childCategories, error: checkError } = await supabase
        .from('email_template_categories')
        .select('id')
        .eq('parent_id', id);
        
      if (checkError) throw checkError;
      
      if (childCategories && childCategories.length > 0) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: `Cannot delete "${name}" because it has subcategories`,
        });
        return;
      }
      
      // Check if the category has templates
      const { data: templates, error: templatesError } = await supabase
        .from('email_templates')
        .select('id')
        .eq('category_id', id);
        
      if (templatesError) throw templatesError;
      
      if (templates && templates.length > 0) {
        toast({
          variant: 'destructive',
          title: 'Error',
          description: `Cannot delete "${name}" because it has ${templates.length} templates`,
        });
        return;
      }

      const { error } = await supabase
        .from('email_template_categories')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setCategories(categories.filter(c => c.id !== id));
      toast({
        title: 'Category Deleted',
        description: `Category "${name}" has been deleted`,
      });
    } catch (error: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to delete category',
      });
    }
  };
  
  const getCategoryById = (id: string | null) => {
    if (!id) return null;
    return categories.find(c => c.id === id) || null;
  };
  
  const getParentCategoryName = (category: Category) => {
    const parent = getCategoryById(category.parent_id);
    return parent ? parent.name : null;
  };
  
  // Get top-level categories (those without a parent)
  const topLevelCategories = categories.filter(c => !c.parent_id);
  
  // Get subcategories for a given parent
  const getSubcategories = (parentId: string) => {
    return categories.filter(c => c.parent_id === parentId);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Template Categories</h3>
        <Button onClick={handleNewCategory} size="sm">
          <Plus className="h-4 w-4 mr-1" />
          New Category
        </Button>
      </div>

      <div className="border rounded-md">
        {loading ? (
          <div className="p-4 space-y-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center space-x-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 w-64 flex-1" />
                <Skeleton className="h-8 w-16" />
              </div>
            ))}
          </div>
        ) : categories.length === 0 ? (
          <div className="p-8 text-center text-muted-foreground">
            No categories found. Add one to get started.
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Parent</TableHead>
                <TableHead>Color</TableHead>
                <TableHead className="w-[120px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {topLevelCategories.map((category) => (
                <div key={`category-wrapper-${category.id}`}>
                  <TableRow>
                    <TableCell className="font-medium">{category.name}</TableCell>
                    <TableCell className="max-w-xs truncate text-muted-foreground">
                      {category.description || '-'}
                    </TableCell>
                    <TableCell>-</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span 
                          className="inline-block w-4 h-4 rounded-full" 
                          style={{ backgroundColor: category.color || '#3b82f6' }}
                        />
                        <span className="text-xs text-muted-foreground">{category.color}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleEdit(category)}
                          title="Edit"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleAddSubcategory(category)}
                          title="Add Subcategory"
                        >
                          <CirclePlus className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDelete(category.id, category.name)}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                  {/* Render subcategories with indentation */}
                  {getSubcategories(category.id).map((subCategory) => (
                    <TableRow key={`subcategory-${subCategory.id}`} className="bg-muted/20">
                      <TableCell className="font-medium pl-8">
                        {subCategory.name}
                      </TableCell>
                      <TableCell className="max-w-xs truncate text-muted-foreground">
                        {subCategory.description || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{category.name}</Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span 
                            className="inline-block w-4 h-4 rounded-full" 
                            style={{ backgroundColor: subCategory.color || '#3b82f6' }}
                          />
                          <span className="text-xs text-muted-foreground">{subCategory.color}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleEdit(subCategory)}
                            title="Edit"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDelete(subCategory.id, subCategory.name)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </div>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Edit Category' : parentId ? 'Add Subcategory' : 'Add New Category'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label className="text-right text-sm">Name:</label>
              <Input 
                className="col-span-3" 
                value={name} 
                onChange={(e) => setName(e.target.value)} 
                placeholder="Category name"
              />
            </div>
            <div className="grid grid-cols-4 items-start gap-4">
              <label className="text-right text-sm">Description:</label>
              <Textarea 
                className="col-span-3 min-h-[80px]" 
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Optional description"
              />
            </div>
            {!parentId && (
              <div className="grid grid-cols-4 items-center gap-4">
                <label className="text-right text-sm">Parent:</label>
                <select 
                  className="col-span-3 p-2 border rounded-md" 
                  value={parentId || ''}
                  onChange={(e) => setParentId(e.target.value || null)}
                >
                  <option value="">None (Top Level)</option>
                  {categories
                    .filter(c => !c.parent_id && (!editingCategory || c.id !== editingCategory.id))
                    .map(category => (
                      <option key={category.id} value={category.id}>
                        {category.name}
                      </option>
                    ))
                  }
                </select>
              </div>
            )}
            <div className="grid grid-cols-4 items-center gap-4">
              <label className="text-right text-sm">Color:</label>
              <div className="col-span-3 flex items-center gap-2">
                <input 
                  type="color" 
                  value={color} 
                  onChange={(e) => setColor(e.target.value)}
                  className="w-10 h-10 rounded-md cursor-pointer"
                />
                <Input 
                  value={color} 
                  onChange={(e) => setColor(e.target.value)}
                  className="w-28"
                />
              </div>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label className="text-right text-sm">Display Order:</label>
              <Input 
                type="number" 
                className="col-span-3" 
                value={displayOrder} 
                onChange={(e) => setDisplayOrder(parseInt(e.target.value) || 0)} 
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
